import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { makeCygnusApiCall } from "../../api/api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";

const FlowUsageMetrics = () => {
  const [data, setData] = useState([]);
  const [latestUpdate, setLatestUpdate] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await makeCygnusApiCall({
          type: "POST",
          payload: {},
          url: "/status/usage_metrics/",
          show_message: false,
          customized_success_message: "Loading dashboard",
          customized_failure_message: "Unable to fetch details",
          customized_error_message: "Error occurred while fetching dashboard, please contact support and attach a screenshot.",
          isblob: false,
        });

        if (response.success) {
          const apiData = response.data;
          setData(apiData);

          const latest = apiData.reduce((max, item) =>
            new Date(item.UpdatedDate) > new Date(max)
              ? item.UpdatedDate
              : max,
            apiData[0]?.UpdatedDate
          );
          setLatestUpdate(latest);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const exportToExcel = () => {
    if (data.length === 0) return;

    const exportData = data.map((item) => ({
      "Flow Name": item.FlowName,
      "Success Count": item.SuccessCount,
      "Failure Count": item.FailCount,
      "Total Runs": item.SuccessCount + item.FailCount,
      "Allowed Limit": item.AllowedCount,
      "Contract Renewal Date": item.ContractRenewalDate
        ? new Date(item.ContractRenewalDate).toLocaleDateString()
        : "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Flow Usage");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const file = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(file, `FlowUsageMetrics_${new Date().toISOString()}.xlsx`);
  };

  if (data.length === 0) {
    return (
      <div style={{ textAlign: "center", fontSize: "16px", marginTop: "20px" }}>
        No flows currently configured...
      </div>
    );
  }

  const categories = data.map(flow => flow.FlowName);
  const minWidth = Math.max(1000, data.length * 150);

  const options = {
    chart: {
      type: "column",
      height: null,
      scrollablePlotArea: {
        minWidth: minWidth,
        scrollPositionX: 0,
      },
    },
    title: {
      text: "Flow Usage Metrics",
      style: { fontSize: "18px", fontWeight: "bold" },
    },
    xAxis: {
      type: "linear",
      categories: null,
      labels: {
        formatter: function () {
          return data[this.value]?.FlowName || '';
        },
        rotation: -45,
        style: {
          fontSize: "10px",
          whiteSpace: "normal",
          width: "100px",
        },
      },
      tickInterval: 1,
      min: 0,
      max: data.length - 1,
      title: { text: "Flows" },
    },
    yAxis: {
      min: 0,
      title: { text: "Run Count" },
      stackLabels: {
        enabled: true,
        allowOverlap: true,
        verticalAlign: "top",
        y: -5,
        formatter: function () {
          return this.total === 0 ? null : this.total;
        },
        style: {
          fontWeight: "bold",
          color: "#333",
          fontSize: "11px",
          textOutline: "none",
          textOverflow: "none"
        },
        overflow: "justify",
        crop: false,
      }
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
    },
    tooltip: {
      shared: true,
      formatter: function () {
        const flow = data[this.x];
        const total = flow ? flow.SuccessCount + flow.FailCount : 0;
        const contractRenewalDate = flow?.ContractRenewalDate
          ? new Date(flow.ContractRenewalDate).toLocaleDateString()
          : "N/A";

        return `
          <b>${flow?.FlowName || ''}</b><br/>
          Contract Renewal: <b>${contractRenewalDate}</b><br/>
          ${this.points
            ?.map(
              p =>
                `<span style="color:${p.color}">\u25CF</span> ${p.series.name}: <b>${p.y}</b><br/>`
            )
            .join("")}
          Total: ${total}
        `;
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
        groupPadding: 0.2,
        pointPadding: 0.05,
        colorByPoint: false,
        borderWidth: 0,
        pointWidth: 30,
        dataLabels: {
          enabled: false,
        },
      },
    },
    series: [
      {
        name: "Success",
        type: "column",
        data: data.map((flow, i) => ({ x: i, y: flow.SuccessCount })),
        color: "#4CAF50",
        stack: "total",
        showInLegend: true,
      },
      {
        name: "Failure",
        type: "column",
        data: data.map((flow, i) => ({ x: i, y: flow.FailCount })),
        color: "#F44336",
        stack: "total",
        showInLegend: true,
      },
      {
        name: "Allowed Limit",
        type: "line",
        data: [
          { x: -0.5, y: data[0]?.AllowedCount || 0 },
          ...data.map((flow, i) => ({ x: i, y: flow.AllowedCount })),
          { x: data.length - 0.5 + 1, y: data.at(-1)?.AllowedCount || 0 },
        ],
        dashStyle: "Dash",
        color: "black",
        marker: { enabled: false },
        enableMouseTracking: false,
        grouping: false,
        tooltip: {
          pointFormat: "Allowed Limit: <b>{point.y}</b><br/>",
        },
        showInLegend: true,
      },
    ],
  };


  return (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "center",
      minHeight: "100vh",
      padding: "40px 20px",
      boxSizing: "border-box",
    }}
  >
    <div style={{ width: "100%", maxWidth: "1600px" }}>
      <div className="row align-items-start justify-content-between mb-3">
        <div className="col-md-8">
          <h1 style={{ fontSize: "32px", marginBottom: "10px" }}>
            Usage Metrics Report
          </h1>
          {latestUpdate && (
            <p style={{ fontSize: "12px", fontStyle: "italic", color: "gray" }}>
              * Data last updated at:{" "}
              {new Date(latestUpdate).toLocaleString()}
            </p>
          )}
        </div>

        {data.length > 0 && (
          <div className="col-md-4 d-flex justify-content-end align-items-start">
            <button
              className="btn btn-success small-font date-select"
              onClick={exportToExcel}
            >
              Export to Excel <FontAwesomeIcon icon={faFileExcel} />
            </button>
          </div>
        )}
      </div>

      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  </div>
);
};

export default FlowUsageMetrics;
